import * as THREE from 'three';
import { createGlowMaterial, createMatteMaterial } from '../../visual';
import type { VendorPreset } from './ShopKind';

export interface StationVendorOptions {
  readonly preset: VendorPreset;
  readonly position: THREE.Vector3;
  readonly facing: number;
}

/**
 * Highly detailed humanoid/alien vendor figures. Each preset has its own
 * anatomy, clothing, accessories and idle animation nuance so they read as
 * specific, memorable characters behind the counter.
 */
export class StationVendor {
  readonly object3D: THREE.Group;
  readonly preset: VendorPreset;

  private readonly head: THREE.Group;
  private readonly torso: THREE.Group;
  private readonly leftArm: THREE.Group;
  private readonly rightArm: THREE.Group;
  private readonly propRefs: Array<{
    mesh: THREE.Mesh;
    pulse?: { speed: number; phase: number };
  }>;
  private time = Math.random() * 10;

  constructor(options: StationVendorOptions) {
    this.preset = options.preset;
    this.object3D = new THREE.Group();
    this.object3D.name = `vendor:${options.preset}`;
    this.object3D.position.copy(options.position);
    this.object3D.rotation.y = options.facing;

    const build = this.resolvePreset(options.preset);
    this.torso = build.torso;
    this.head = build.head;
    this.leftArm = build.leftArm;
    this.rightArm = build.rightArm;
    this.propRefs = build.props;

    this.object3D.add(build.root);
  }

  update(dt: number): void {
    this.time += dt;

    // Soft breathing + sway.
    const breathe = Math.sin(this.time * 1.3) * 0.02;
    this.torso.position.y = breathe + this.torso.userData['baseY'];
    this.torso.rotation.y = Math.sin(this.time * 0.3) * 0.05;

    // Head subtle movement.
    this.head.rotation.y = Math.sin(this.time * 0.27) * 0.14;
    this.head.rotation.x = Math.sin(this.time * 0.21) * 0.04;

    // Arms slight idle movement.
    this.leftArm.rotation.x = Math.sin(this.time * 1.05) * 0.05 - 0.08;
    this.rightArm.rotation.x = Math.sin(this.time * 1.2 + 0.7) * 0.05 - 0.06;
    this.leftArm.rotation.z = Math.sin(this.time * 0.6) * 0.025;
    this.rightArm.rotation.z = Math.sin(this.time * 0.55 + 0.4) * 0.025;

    // Floating props (tablets, tool lights, etc.).
    for (const ref of this.propRefs) {
      if (ref.pulse) {
        const mat = ref.mesh.material as THREE.MeshBasicMaterial;
        mat.opacity =
          ref.pulse.phase +
          Math.sin(this.time * ref.pulse.speed) * 0.15;
      }
      ref.mesh.position.y =
        (ref.mesh.userData['baseY'] as number) +
        Math.sin(this.time * 0.9 + (ref.mesh.userData['drift'] as number)) *
          0.05;
    }
  }

  private resolvePreset(preset: VendorPreset): PresetBuild {
    switch (preset) {
      case 'engineer-human':
        return this.buildEngineer();
      case 'arms-human':
        return this.buildArmsmaster();
      case 'technician-alien':
        return this.buildTechnicianAlien();
      case 'trader-alien':
        return this.buildTraderAlien();
    }
  }

  // ---------------------------------------------------------------------
  // Detailed anatomy helpers
  // ---------------------------------------------------------------------

  private buildHumanHead(opts: {
    skin: number;
    hair: number;
    eyesGlow: number;
    visorColor?: number;
    headband?: number;
    beard?: number;
  }): THREE.Group {
    const head = new THREE.Group();
    const skinMat = createMatteMaterial({ color: opts.skin, roughness: 0.65 });
    const hairMat = createMatteMaterial({ color: opts.hair, roughness: 0.8 });

    // Skull (ovoid).
    const skull = new THREE.Mesh(
      new THREE.SphereGeometry(0.19, 24, 18),
      skinMat
    );
    skull.scale.set(0.96, 1.1, 1.0);
    head.add(skull);

    // Jaw (small box adding chin definition).
    const jaw = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.12, 0.18),
      skinMat
    );
    jaw.position.set(0, -0.09, 0.01);
    head.add(jaw);

    // Brow ridge.
    const brow = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.04, 0.08),
      skinMat
    );
    brow.position.set(0, 0.05, 0.17);
    head.add(brow);

    // Nose bridge.
    const nose = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.1, 0.1),
      skinMat
    );
    nose.position.set(0, -0.01, 0.2);
    head.add(nose);

    // Mouth (small dark line).
    const mouth = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.02, 0.05),
      createMatteMaterial({ color: 0x2a1e1a, roughness: 0.9 })
    );
    mouth.position.set(0, -0.07, 0.18);
    head.add(mouth);

    // Ears.
    const earGeo = new THREE.SphereGeometry(0.04, 10, 8);
    const earL = new THREE.Mesh(earGeo, skinMat);
    earL.position.set(-0.19, -0.01, 0);
    earL.scale.set(0.7, 1.1, 0.8);
    head.add(earL);
    const earR = earL.clone();
    earR.position.x = 0.19;
    head.add(earR);

    // Hair block.
    const hair = new THREE.Mesh(
      new THREE.SphereGeometry(0.205, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2),
      hairMat
    );
    hair.scale.set(1.02, 0.7, 1.05);
    hair.position.y = 0.08;
    head.add(hair);

    if (opts.visorColor !== undefined) {
      const visorBand = new THREE.Mesh(
        new THREE.BoxGeometry(0.32, 0.07, 0.1),
        createMatteMaterial({ color: 0x0a0d14, roughness: 0.5 })
      );
      visorBand.position.set(0, 0.04, 0.18);
      head.add(visorBand);

      const visorGlow = new THREE.Mesh(
        new THREE.BoxGeometry(0.3, 0.04, 0.03),
        createGlowMaterial(opts.visorColor, 0.95, true)
      );
      visorGlow.position.set(0, 0.04, 0.235);
      head.add(visorGlow);
    } else {
      // Eye whites + pupils.
      const eyeWhite = createMatteMaterial({ color: 0xffffff, roughness: 0.3 });
      const eyeGeo = new THREE.SphereGeometry(0.028, 10, 8);
      const eyeL = new THREE.Mesh(eyeGeo, eyeWhite);
      eyeL.position.set(-0.07, 0.02, 0.18);
      head.add(eyeL);
      const eyeR = eyeL.clone();
      eyeR.position.x = 0.07;
      head.add(eyeR);

      const pupilMat = createGlowMaterial(opts.eyesGlow, 1, true);
      const pupilGeo = new THREE.SphereGeometry(0.016, 8, 6);
      const pupilL = new THREE.Mesh(pupilGeo, pupilMat);
      pupilL.position.set(-0.07, 0.02, 0.205);
      head.add(pupilL);
      const pupilR = pupilL.clone();
      pupilR.position.x = 0.07;
      head.add(pupilR);
    }

    if (opts.headband !== undefined) {
      const band = new THREE.Mesh(
        new THREE.TorusGeometry(0.2, 0.022, 8, 24),
        createMatteMaterial({
          color: opts.headband,
          roughness: 0.5,
          emissive: opts.headband,
          emissiveIntensity: 0.3
        })
      );
      band.rotation.x = Math.PI / 2;
      band.position.y = 0.13;
      band.scale.set(1.02, 1.08, 1.0);
      head.add(band);
    }

    if (opts.beard !== undefined) {
      const beard = new THREE.Mesh(
        new THREE.SphereGeometry(0.14, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2),
        createMatteMaterial({ color: opts.beard, roughness: 0.85 })
      );
      beard.rotation.x = Math.PI;
      beard.scale.set(0.85, 0.8, 0.85);
      beard.position.set(0, -0.13, 0.04);
      head.add(beard);
    }

    return head;
  }

  private buildHumanArm(opts: {
    sleeve: number;
    sleeveDark: number;
    skin: number;
    glove: number;
    gloveAccent: number;
    side: -1 | 1;
  }): THREE.Group {
    const arm = new THREE.Group();
    const sleeveMat = createMatteMaterial({ color: opts.sleeve, roughness: 0.7 });
    const sleeveDarkMat = createMatteMaterial({
      color: opts.sleeveDark,
      roughness: 0.8
    });
    const skinMat = createMatteMaterial({ color: opts.skin, roughness: 0.65 });
    const gloveMat = createMatteMaterial({ color: opts.glove, roughness: 0.6 });
    const accentMat = createMatteMaterial({
      color: opts.gloveAccent,
      roughness: 0.4,
      emissive: opts.gloveAccent,
      emissiveIntensity: 0.22
    });

    // Shoulder (sphere).
    const shoulder = new THREE.Mesh(
      new THREE.SphereGeometry(0.13, 12, 10),
      sleeveMat
    );
    shoulder.position.y = -0.05;
    arm.add(shoulder);

    // Upper sleeve.
    const upper = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.085, 0.45, 10),
      sleeveMat
    );
    upper.position.y = -0.28;
    arm.add(upper);

    // Elbow pad.
    const elbow = new THREE.Mesh(
      new THREE.SphereGeometry(0.09, 10, 8),
      sleeveDarkMat
    );
    elbow.position.y = -0.52;
    arm.add(elbow);

    // Lower sleeve (with accent cuff).
    const lower = new THREE.Mesh(
      new THREE.CylinderGeometry(0.084, 0.075, 0.4, 10),
      sleeveDarkMat
    );
    lower.position.y = -0.73;
    arm.add(lower);

    const cuff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.088, 0.088, 0.07, 14),
      accentMat
    );
    cuff.position.y = -0.94;
    arm.add(cuff);

    // Hand (glove).
    const hand = new THREE.Mesh(
      new THREE.SphereGeometry(0.085, 12, 10),
      gloveMat
    );
    hand.scale.set(0.85, 1.1, 1);
    hand.position.y = -1.02;
    arm.add(hand);

    // Small wrist accent.
    const wristGlow = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.02, 0.04),
      accentMat
    );
    wristGlow.position.set(opts.side * 0.04, -0.97, 0.05);
    arm.add(wristGlow);

    return arm;
  }

  // ---------------------------------------------------------------------
  // Preset 1: Engineer (human)
  // ---------------------------------------------------------------------

  private buildEngineer(): PresetBuild {
    const root = new THREE.Group();
    const props: PresetBuild['props'] = [];

    const suitMat = createMatteMaterial({ color: 0x2f4e7a, roughness: 0.7 });
    const suitDarkMat = createMatteMaterial({
      color: 0x17263e,
      roughness: 0.8
    });
    const trimMat = createMatteMaterial({ color: 0x1a1e2c, roughness: 0.7 });
    const accentMat = createMatteMaterial({
      color: 0x9fd9ff,
      roughness: 0.4,
      emissive: 0x6ac0ff,
      emissiveIntensity: 0.35
    });

    const pelvis = new THREE.Group();
    pelvis.position.y = 1.05;
    root.add(pelvis);

    // Legs with knee pads and boots.
    for (const side of [-1, 1] as const) {
      const hip = new THREE.Mesh(
        new THREE.CylinderGeometry(0.1, 0.12, 0.45, 10),
        suitMat
      );
      hip.position.set(side * 0.12, -0.3, 0);
      pelvis.add(hip);

      const knee = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 10, 8),
        suitDarkMat
      );
      knee.position.set(side * 0.12, -0.55, 0);
      pelvis.add(knee);

      const shin = new THREE.Mesh(
        new THREE.CylinderGeometry(0.09, 0.1, 0.42, 10),
        suitDarkMat
      );
      shin.position.set(side * 0.12, -0.78, 0);
      pelvis.add(shin);

      const boot = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.15, 0.28),
        trimMat
      );
      boot.position.set(side * 0.12, -1.02, 0.05);
      pelvis.add(boot);

      const bootToe = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.06, 0.05),
        accentMat
      );
      bootToe.position.set(side * 0.12, -1.02, 0.21);
      pelvis.add(bootToe);
    }

    // Utility belt.
    const belt = new THREE.Mesh(
      new THREE.BoxGeometry(0.62, 0.14, 0.38),
      trimMat
    );
    belt.position.y = 0;
    pelvis.add(belt);

    const buckle = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.12, 0.06),
      accentMat
    );
    buckle.position.set(0, 0, 0.22);
    pelvis.add(buckle);

    // Tool pouches on the belt.
    for (const side of [-1, 1] as const) {
      const pouch = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.18, 0.1),
        suitDarkMat
      );
      pouch.position.set(side * 0.22, -0.03, 0.22);
      pelvis.add(pouch);
    }

    const torso = new THREE.Group();
    torso.position.y = 0.05;
    torso.userData['baseY'] = 0.05;
    pelvis.add(torso);

    // Chest block.
    const chest = new THREE.Mesh(
      new THREE.BoxGeometry(0.68, 0.82, 0.4),
      suitMat
    );
    chest.position.y = 0.43;
    torso.add(chest);

    // Chest plate overlay (armor).
    const chestPlate = new THREE.Mesh(
      new THREE.BoxGeometry(0.45, 0.55, 0.04),
      suitDarkMat
    );
    chestPlate.position.set(0, 0.48, 0.22);
    torso.add(chestPlate);

    // Badge/emblem on chest (shop identity).
    const badge = new THREE.Mesh(
      new THREE.CircleGeometry(0.08, 20),
      accentMat
    );
    badge.position.set(0.15, 0.6, 0.24);
    torso.add(badge);

    // Diagonal tool strap.
    const strap = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.86, 0.06),
      trimMat
    );
    strap.position.set(-0.15, 0.42, 0.22);
    strap.rotation.z = 0.35;
    torso.add(strap);

    // Shoulder pads (bulky, engineer-y).
    for (const side of [-1, 1] as const) {
      const pad = new THREE.Mesh(
        new THREE.SphereGeometry(0.22, 14, 10),
        suitDarkMat
      );
      pad.scale.set(1.15, 0.7, 1.15);
      pad.position.set(side * 0.34, 0.8, 0);
      torso.add(pad);

      const padAccent = new THREE.Mesh(
        new THREE.TorusGeometry(0.2, 0.02, 6, 18),
        accentMat
      );
      padAccent.rotation.y = Math.PI / 2;
      padAccent.scale.set(1.2, 0.6, 1);
      padAccent.position.set(side * 0.34, 0.8, 0);
      torso.add(padAccent);
    }

    // Collar/neck.
    const neck = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.12, 0.13, 12),
      createMatteMaterial({ color: 0xdbb08f, roughness: 0.65 })
    );
    neck.position.y = 0.92;
    torso.add(neck);

    const collar = new THREE.Mesh(
      new THREE.BoxGeometry(0.44, 0.08, 0.36),
      accentMat
    );
    collar.position.y = 0.86;
    torso.add(collar);

    // Arms.
    const armL = this.buildHumanArm({
      sleeve: 0x2f4e7a,
      sleeveDark: 0x17263e,
      skin: 0xdbb08f,
      glove: 0x1a1e2c,
      gloveAccent: 0x9fd9ff,
      side: -1
    });
    armL.position.set(-0.35, 0.85, 0);
    torso.add(armL);

    const armR = this.buildHumanArm({
      sleeve: 0x2f4e7a,
      sleeveDark: 0x17263e,
      skin: 0xdbb08f,
      glove: 0x1a1e2c,
      gloveAccent: 0x9fd9ff,
      side: 1
    });
    armR.position.set(0.35, 0.85, 0);
    torso.add(armR);

    // Head with visor (engineer signature).
    const head = this.buildHumanHead({
      skin: 0xdbb08f,
      hair: 0x2a1608,
      eyesGlow: 0x9fd9ff,
      visorColor: 0x9fd9ff,
      headband: 0x6ac0ff
    });
    head.position.y = 1.12;
    torso.add(head);

    // Floating holographic blueprint panel above the counter.
    const holoPanel = new THREE.Mesh(
      new THREE.PlaneGeometry(0.6, 0.42),
      createGlowMaterial(0x9fd9ff, 0.55, true)
    );
    holoPanel.position.set(0.5, 1.35, -0.3);
    holoPanel.rotation.y = 0.7;
    torso.add(holoPanel);
    holoPanel.userData['baseY'] = 1.35;
    holoPanel.userData['drift'] = 0;
    props.push({
      mesh: holoPanel,
      pulse: { speed: 2.6, phase: 0.7 }
    });

    return { root, torso, head, leftArm: armL, rightArm: armR, props };
  }

  // ---------------------------------------------------------------------
  // Preset 2: Armsmaster (human)
  // ---------------------------------------------------------------------

  private buildArmsmaster(): PresetBuild {
    const root = new THREE.Group();
    const props: PresetBuild['props'] = [];

    const coatMat = createMatteMaterial({ color: 0x4b1e1e, roughness: 0.72 });
    const coatDarkMat = createMatteMaterial({
      color: 0x2a0a0a,
      roughness: 0.82
    });
    const leatherMat = createMatteMaterial({ color: 0x1a0608, roughness: 0.85 });
    const accentMat = createMatteMaterial({
      color: 0xff8a8a,
      roughness: 0.35,
      emissive: 0xff5a5a,
      emissiveIntensity: 0.4
    });

    const pelvis = new THREE.Group();
    pelvis.position.y = 1.07;
    root.add(pelvis);

    // Legs with reinforced greaves.
    for (const side of [-1, 1] as const) {
      const thigh = new THREE.Mesh(
        new THREE.CylinderGeometry(0.12, 0.13, 0.5, 10),
        coatDarkMat
      );
      thigh.position.set(side * 0.13, -0.33, 0);
      pelvis.add(thigh);

      const shinGuard = new THREE.Mesh(
        new THREE.BoxGeometry(0.22, 0.55, 0.22),
        leatherMat
      );
      shinGuard.position.set(side * 0.13, -0.82, 0.02);
      pelvis.add(shinGuard);

      const shinAccent = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.48, 0.22),
        accentMat
      );
      shinAccent.position.set(side * 0.02, -0.82, 0.13);
      pelvis.add(shinAccent);

      const boot = new THREE.Mesh(
        new THREE.BoxGeometry(0.22, 0.12, 0.32),
        leatherMat
      );
      boot.position.set(side * 0.13, -1.08, 0.05);
      pelvis.add(boot);
    }

    // Heavy ammo belt + side holster.
    const belt = new THREE.Mesh(
      new THREE.BoxGeometry(0.68, 0.16, 0.42),
      leatherMat
    );
    belt.position.y = 0;
    pelvis.add(belt);

    const bullets = [-0.22, -0.08, 0.06, 0.22];
    for (const bx of bullets) {
      const bullet = new THREE.Mesh(
        new THREE.CylinderGeometry(0.03, 0.03, 0.1, 8),
        accentMat
      );
      bullet.rotation.z = Math.PI / 2;
      bullet.position.set(bx, 0.04, 0.23);
      pelvis.add(bullet);
    }

    const holster = new THREE.Mesh(
      new THREE.BoxGeometry(0.16, 0.3, 0.18),
      leatherMat
    );
    holster.position.set(0.28, -0.12, 0.22);
    pelvis.add(holster);

    const pistolGrip = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.12, 0.14),
      coatDarkMat
    );
    pistolGrip.position.set(0.28, -0.22, 0.22);
    pelvis.add(pistolGrip);

    const torso = new THREE.Group();
    torso.position.y = 0.05;
    torso.userData['baseY'] = 0.05;
    pelvis.add(torso);

    // Torso (chest + tactical vest).
    const chest = new THREE.Mesh(
      new THREE.BoxGeometry(0.74, 0.8, 0.42),
      coatMat
    );
    chest.position.y = 0.42;
    torso.add(chest);

    const vest = new THREE.Mesh(
      new THREE.BoxGeometry(0.62, 0.78, 0.1),
      leatherMat
    );
    vest.position.set(0, 0.42, 0.2);
    torso.add(vest);

    // Chest magazine pouches.
    for (const x of [-0.18, 0.18]) {
      const pouch = new THREE.Mesh(
        new THREE.BoxGeometry(0.14, 0.25, 0.08),
        coatDarkMat
      );
      pouch.position.set(x, 0.42, 0.28);
      torso.add(pouch);

      const clip = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.22, 0.06),
        accentMat
      );
      clip.position.set(x, 0.54, 0.32);
      torso.add(clip);
    }

    // Heavy shoulder pads (armsmaster signature).
    for (const side of [-1, 1] as const) {
      const pad = new THREE.Mesh(
        new THREE.SphereGeometry(0.24, 14, 10),
        coatDarkMat
      );
      pad.scale.set(1.2, 0.75, 1.2);
      pad.position.set(side * 0.38, 0.82, 0);
      torso.add(pad);

      // Spikes on shoulder.
      const spike = new THREE.Mesh(
        new THREE.ConeGeometry(0.06, 0.18, 6),
        accentMat
      );
      spike.rotation.z = side * -Math.PI / 6;
      spike.position.set(side * 0.5, 0.9, 0);
      torso.add(spike);
    }

    const collar = new THREE.Mesh(
      new THREE.BoxGeometry(0.54, 0.12, 0.4),
      coatDarkMat
    );
    collar.position.y = 0.84;
    torso.add(collar);

    // Arms.
    const armL = this.buildHumanArm({
      sleeve: 0x4b1e1e,
      sleeveDark: 0x2a0a0a,
      skin: 0xc9937d,
      glove: 0x1a0608,
      gloveAccent: 0xff5a5a,
      side: -1
    });
    armL.position.set(-0.39, 0.85, 0);
    torso.add(armL);

    const armR = this.buildHumanArm({
      sleeve: 0x4b1e1e,
      sleeveDark: 0x2a0a0a,
      skin: 0xc9937d,
      glove: 0x1a0608,
      gloveAccent: 0xff5a5a,
      side: 1
    });
    armR.position.set(0.39, 0.85, 0);
    torso.add(armR);

    // Head with scar & shaved sides.
    const head = this.buildHumanHead({
      skin: 0xc9937d,
      hair: 0x1a1a1a,
      eyesGlow: 0xff8a8a,
      headband: 0xff5a5a
    });
    head.position.y = 1.12;
    torso.add(head);

    // Floating weapon hologram above counter.
    const holoGun = new THREE.Mesh(
      new THREE.BoxGeometry(0.7, 0.14, 0.1),
      createGlowMaterial(0xff8a8a, 0.5, true)
    );
    holoGun.position.set(0.5, 1.35, -0.3);
    holoGun.rotation.y = 0.6;
    torso.add(holoGun);
    holoGun.userData['baseY'] = 1.35;
    holoGun.userData['drift'] = 0.4;
    props.push({ mesh: holoGun, pulse: { speed: 2.4, phase: 0.6 } });

    return { root, torso, head, leftArm: armL, rightArm: armR, props };
  }

  // ---------------------------------------------------------------------
  // Preset 3: Technician alien (tall, four-eyed, robed)
  // ---------------------------------------------------------------------

  private buildTechnicianAlien(): PresetBuild {
    const root = new THREE.Group();
    const props: PresetBuild['props'] = [];
    root.scale.setScalar(1.12);

    const robeMat = createMatteMaterial({ color: 0x3a2a12, roughness: 0.8 });
    const robeDarkMat = createMatteMaterial({
      color: 0x1c1308,
      roughness: 0.85
    });
    const hoodMat = createMatteMaterial({ color: 0x2a1d0a, roughness: 0.85 });
    const accentMat = createMatteMaterial({
      color: 0xffc378,
      roughness: 0.4,
      emissive: 0xffa063,
      emissiveIntensity: 0.42
    });
    const skinMat = createMatteMaterial({ color: 0xd4b98c, roughness: 0.7 });
    const eyesMat = createGlowMaterial(0xffb170, 1, true);

    const pelvis = new THREE.Group();
    pelvis.position.y = 1.1;
    root.add(pelvis);

    // Layered robe (two cones stacked for visual depth).
    const lowerRobe = new THREE.Mesh(
      new THREE.ConeGeometry(0.48, 1.1, 16, 1, true),
      robeDarkMat
    );
    lowerRobe.position.y = -0.55;
    pelvis.add(lowerRobe);

    const midRobe = new THREE.Mesh(
      new THREE.ConeGeometry(0.4, 0.7, 16, 1, true),
      robeMat
    );
    midRobe.position.y = -0.2;
    pelvis.add(midRobe);

    // Hem trim.
    const hemTrim = new THREE.Mesh(
      new THREE.TorusGeometry(0.48, 0.025, 8, 32),
      accentMat
    );
    hemTrim.rotation.x = Math.PI / 2;
    hemTrim.position.y = -1.05;
    pelvis.add(hemTrim);

    // Vertical robe trim stripes.
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2 + Math.PI / 8;
      const stripe = new THREE.Mesh(
        new THREE.BoxGeometry(0.025, 0.9, 0.04),
        accentMat
      );
      stripe.position.set(
        Math.cos(angle) * 0.41,
        -0.5,
        Math.sin(angle) * 0.41
      );
      pelvis.add(stripe);
    }

    const torso = new THREE.Group();
    torso.position.y = 0.05;
    torso.userData['baseY'] = 0.05;
    pelvis.add(torso);

    // Robed torso — boxy cowl rather than chest.
    const torsoBody = new THREE.Mesh(
      new THREE.BoxGeometry(0.6, 0.75, 0.38),
      robeMat
    );
    torsoBody.position.y = 0.32;
    torso.add(torsoBody);

    // Sash across the torso.
    const sash = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.85, 0.04),
      accentMat
    );
    sash.position.set(-0.12, 0.35, 0.2);
    sash.rotation.z = 0.25;
    torso.add(sash);

    // Central chest crest (identity piece).
    const crest = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 0.48, 0.06),
      createMatteMaterial({
        color: 0xffa063,
        roughness: 0.45,
        emissive: 0xffa063,
        emissiveIntensity: 0.25
      })
    );
    crest.position.set(0.1, 0.38, 0.21);
    torso.add(crest);

    // Cowl/shoulder drape.
    const cowl = new THREE.Mesh(
      new THREE.SphereGeometry(0.35, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2),
      hoodMat
    );
    cowl.scale.set(1.1, 0.6, 1.0);
    cowl.position.y = 0.68;
    torso.add(cowl);

    // Elongated alien arms with slim proportions.
    const armL = this.buildAlienArm({
      sleeve: 0x3a2a12,
      sleeveDark: 0x1c1308,
      skin: 0xd4b98c,
      accent: 0xffa063
    });
    armL.position.set(-0.34, 0.65, 0);
    torso.add(armL);

    const armR = this.buildAlienArm({
      sleeve: 0x3a2a12,
      sleeveDark: 0x1c1308,
      skin: 0xd4b98c,
      accent: 0xffa063
    });
    armR.position.set(0.34, 0.65, 0);
    torso.add(armR);

    // Elongated head with 4 eyes and crests.
    const head = new THREE.Group();
    head.position.y = 0.95;
    torso.add(head);

    const skull = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 22, 18),
      skinMat
    );
    skull.scale.set(0.85, 1.3, 0.95);
    head.add(skull);

    // Top crest.
    const topCrest = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.34, 0.22),
      accentMat
    );
    topCrest.position.y = 0.24;
    head.add(topCrest);

    // Cheek plate crests.
    for (const side of [-1, 1] as const) {
      const cheek = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.22, 0.1),
        accentMat
      );
      cheek.position.set(side * 0.14, -0.02, 0);
      head.add(cheek);
    }

    // 4 eyes (2 rows).
    const eyeGeo = new THREE.SphereGeometry(0.025, 8, 6);
    for (const side of [-1, 1] as const) {
      const top = new THREE.Mesh(eyeGeo, eyesMat);
      top.position.set(side * 0.05, 0.03, 0.15);
      head.add(top);
      const bot = new THREE.Mesh(eyeGeo, eyesMat);
      bot.position.set(side * 0.09, -0.08, 0.14);
      head.add(bot);
    }

    // Breathing vent/mouth grill.
    const mouth = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.04, 0.04),
      createMatteMaterial({ color: 0x2a1a0a, roughness: 0.9 })
    );
    mouth.position.set(0, -0.14, 0.16);
    head.add(mouth);

    // Hood.
    const hood = new THREE.Mesh(
      new THREE.SphereGeometry(0.27, 18, 14, 0, Math.PI * 2, 0, Math.PI / 2),
      hoodMat
    );
    hood.scale.set(1.15, 1.2, 1.15);
    hood.position.y = 0.05;
    head.add(hood);

    // Floating holographic circuit diagram.
    const circuitHolo = new THREE.Mesh(
      new THREE.CircleGeometry(0.35, 24),
      createGlowMaterial(0xffc378, 0.55, true)
    );
    circuitHolo.position.set(0.5, 1.35, -0.3);
    circuitHolo.rotation.y = 0.8;
    torso.add(circuitHolo);
    circuitHolo.userData['baseY'] = 1.35;
    circuitHolo.userData['drift'] = 0.2;
    props.push({ mesh: circuitHolo, pulse: { speed: 2.2, phase: 0.65 } });

    return { root, torso, head, leftArm: armL, rightArm: armR, props };
  }

  // ---------------------------------------------------------------------
  // Preset 4: Trader alien (stout, bearded, opulent)
  // ---------------------------------------------------------------------

  private buildTraderAlien(): PresetBuild {
    const root = new THREE.Group();
    const props: PresetBuild['props'] = [];
    root.scale.setScalar(0.95);

    const robeMat = createMatteMaterial({ color: 0x4a3410, roughness: 0.78 });
    const robeDarkMat = createMatteMaterial({ color: 0x2a1e08, roughness: 0.85 });
    const trimGold = createMatteMaterial({
      color: 0xf5c542,
      roughness: 0.4,
      emissive: 0xffd389,
      emissiveIntensity: 0.38
    });
    const skinMat = createMatteMaterial({ color: 0x8d7040, roughness: 0.72 });
    const eyesMat = createGlowMaterial(0xffd770, 1, true);

    const pelvis = new THREE.Group();
    pelvis.position.y = 1.0;
    root.add(pelvis);

    // Opulent wide robe.
    const outerRobe = new THREE.Mesh(
      new THREE.ConeGeometry(0.55, 1.1, 18, 1, true),
      robeMat
    );
    outerRobe.position.y = -0.55;
    pelvis.add(outerRobe);

    const innerRobe = new THREE.Mesh(
      new THREE.ConeGeometry(0.42, 0.75, 18, 1, true),
      robeDarkMat
    );
    innerRobe.position.y = -0.37;
    pelvis.add(innerRobe);

    // Gold hem (thick trim).
    const hem = new THREE.Mesh(
      new THREE.TorusGeometry(0.54, 0.04, 10, 36),
      trimGold
    );
    hem.rotation.x = Math.PI / 2;
    hem.position.y = -1.05;
    pelvis.add(hem);

    // Central front vent (robe opening).
    const vent = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 1.0, 0.02),
      robeDarkMat
    );
    vent.position.set(0, -0.5, 0.5);
    pelvis.add(vent);

    // Sash with jewels.
    const sashBelt = new THREE.Mesh(
      new THREE.TorusGeometry(0.45, 0.08, 12, 32),
      trimGold
    );
    sashBelt.rotation.x = Math.PI / 2;
    sashBelt.position.y = -0.1;
    pelvis.add(sashBelt);

    for (let i = 0; i < 3; i++) {
      const angle = (i - 1) * 0.35;
      const jewel = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 12, 10),
        createGlowMaterial(0xffe38a, 0.95, true)
      );
      jewel.position.set(Math.sin(angle) * 0.45, -0.1, Math.cos(angle) * 0.45);
      pelvis.add(jewel);
    }

    const torso = new THREE.Group();
    torso.position.y = 0.05;
    torso.userData['baseY'] = 0.05;
    pelvis.add(torso);

    // Barrel-chested torso.
    const chest = new THREE.Mesh(
      new THREE.BoxGeometry(0.68, 0.78, 0.44),
      robeMat
    );
    chest.position.y = 0.38;
    torso.add(chest);

    // Necklace / pectoral piece.
    const pectoral = new THREE.Mesh(
      new THREE.TorusGeometry(0.22, 0.035, 10, 24),
      trimGold
    );
    pectoral.rotation.x = Math.PI / 2;
    pectoral.position.y = 0.72;
    torso.add(pectoral);

    const medallion = new THREE.Mesh(
      new THREE.CircleGeometry(0.09, 20),
      trimGold
    );
    medallion.position.set(0, 0.55, 0.23);
    torso.add(medallion);

    // Wide collar.
    const collar = new THREE.Mesh(
      new THREE.BoxGeometry(0.52, 0.1, 0.42),
      robeDarkMat
    );
    collar.position.y = 0.8;
    torso.add(collar);

    const collarAccent = new THREE.Mesh(
      new THREE.BoxGeometry(0.54, 0.04, 0.44),
      trimGold
    );
    collarAccent.position.y = 0.85;
    torso.add(collarAccent);

    // Opulent shoulder pads.
    for (const side of [-1, 1] as const) {
      const pad = new THREE.Mesh(
        new THREE.SphereGeometry(0.24, 14, 10),
        robeDarkMat
      );
      pad.scale.set(1.2, 0.65, 1.2);
      pad.position.set(side * 0.36, 0.78, 0);
      torso.add(pad);

      const padGlow = new THREE.Mesh(
        new THREE.TorusGeometry(0.22, 0.02, 6, 16),
        trimGold
      );
      padGlow.rotation.y = Math.PI / 2;
      padGlow.scale.set(1.2, 0.6, 1);
      padGlow.position.set(side * 0.36, 0.78, 0);
      torso.add(padGlow);
    }

    // Arms.
    const armL = this.buildAlienArm({
      sleeve: 0x4a3410,
      sleeveDark: 0x2a1e08,
      skin: 0x8d7040,
      accent: 0xffd389
    });
    armL.position.set(-0.34, 0.72, 0);
    torso.add(armL);

    const armR = this.buildAlienArm({
      sleeve: 0x4a3410,
      sleeveDark: 0x2a1e08,
      skin: 0x8d7040,
      accent: 0xffd389
    });
    armR.position.set(0.34, 0.72, 0);
    torso.add(armR);

    // Head with trader features.
    const head = new THREE.Group();
    head.position.y = 0.95;
    torso.add(head);

    const skull = new THREE.Mesh(
      new THREE.SphereGeometry(0.2, 22, 18),
      skinMat
    );
    skull.scale.set(1.0, 1.15, 1.0);
    head.add(skull);

    // Forehead plate.
    const foreheadPlate = new THREE.Mesh(
      new THREE.BoxGeometry(0.16, 0.08, 0.1),
      trimGold
    );
    foreheadPlate.position.set(0, 0.13, 0.17);
    head.add(foreheadPlate);

    // Single large eye monocle + small secondary eye.
    const monoFrame = new THREE.Mesh(
      new THREE.TorusGeometry(0.06, 0.012, 6, 16),
      trimGold
    );
    monoFrame.position.set(-0.07, 0.02, 0.19);
    head.add(monoFrame);

    const monoLens = new THREE.Mesh(
      new THREE.CircleGeometry(0.055, 16),
      createGlowMaterial(0xffe38a, 0.85, true)
    );
    monoLens.position.set(-0.07, 0.02, 0.195);
    head.add(monoLens);

    const plainEye = new THREE.Mesh(
      new THREE.SphereGeometry(0.028, 10, 8),
      eyesMat
    );
    plainEye.position.set(0.08, 0.02, 0.185);
    head.add(plainEye);

    // Thick beard.
    const beard = new THREE.Mesh(
      new THREE.SphereGeometry(0.17, 18, 14, 0, Math.PI * 2, 0, Math.PI / 2),
      createMatteMaterial({ color: 0xd4a34a, roughness: 0.9 })
    );
    beard.rotation.x = Math.PI;
    beard.scale.set(1.1, 1.1, 1.1);
    beard.position.set(0, -0.12, 0.02);
    head.add(beard);

    // Braided mustache tips.
    for (const side of [-1, 1] as const) {
      const braid = new THREE.Mesh(
        new THREE.CylinderGeometry(0.018, 0.012, 0.18, 6),
        createMatteMaterial({ color: 0xd4a34a, roughness: 0.85 })
      );
      braid.position.set(side * 0.13, -0.2, 0.14);
      braid.rotation.z = side * -0.25;
      head.add(braid);

      const bead = new THREE.Mesh(
        new THREE.SphereGeometry(0.022, 8, 6),
        trimGold
      );
      bead.position.set(side * 0.15, -0.28, 0.18);
      head.add(bead);
    }

    // Floating tablet / credit hologram.
    const tablet = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.28, 0.02),
      createGlowMaterial(0xffe38a, 0.65, true)
    );
    tablet.position.set(0.45, 1.3, -0.3);
    tablet.rotation.y = 0.5;
    tablet.rotation.x = -0.2;
    torso.add(tablet);
    tablet.userData['baseY'] = 1.3;
    tablet.userData['drift'] = 0.3;
    props.push({ mesh: tablet, pulse: { speed: 2.0, phase: 0.7 } });

    return { root, torso, head, leftArm: armL, rightArm: armR, props };
  }

  private buildAlienArm(opts: {
    sleeve: number;
    sleeveDark: number;
    skin: number;
    accent: number;
  }): THREE.Group {
    const arm = new THREE.Group();
    const sleeveMat = createMatteMaterial({ color: opts.sleeve, roughness: 0.75 });
    const sleeveDarkMat = createMatteMaterial({
      color: opts.sleeveDark,
      roughness: 0.82
    });
    const skinMat = createMatteMaterial({ color: opts.skin, roughness: 0.72 });
    const accentMat = createMatteMaterial({
      color: opts.accent,
      roughness: 0.4,
      emissive: opts.accent,
      emissiveIntensity: 0.3
    });

    const shoulder = new THREE.Mesh(
      new THREE.SphereGeometry(0.12, 12, 10),
      sleeveMat
    );
    shoulder.position.y = -0.05;
    arm.add(shoulder);

    const upper = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.06, 0.5, 10),
      sleeveMat
    );
    upper.position.y = -0.3;
    arm.add(upper);

    const elbow = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 10, 8),
      sleeveDarkMat
    );
    elbow.position.y = -0.58;
    arm.add(elbow);

    // Bared forearm skin.
    const forearm = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.055, 0.52, 10),
      skinMat
    );
    forearm.position.y = -0.85;
    arm.add(forearm);

    // Bracelet.
    const bracelet = new THREE.Mesh(
      new THREE.TorusGeometry(0.065, 0.015, 6, 14),
      accentMat
    );
    bracelet.rotation.x = Math.PI / 2;
    bracelet.position.y = -1.08;
    arm.add(bracelet);

    // 4-finger hand.
    const palm = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 0.08, 0.08),
      skinMat
    );
    palm.position.y = -1.17;
    arm.add(palm);

    for (let i = 0; i < 3; i++) {
      const finger = new THREE.Mesh(
        new THREE.CylinderGeometry(0.018, 0.014, 0.13, 6),
        skinMat
      );
      finger.position.set(-0.055 + i * 0.05, -1.25, 0.02);
      arm.add(finger);
    }

    return arm;
  }
}

interface PresetBuild {
  readonly root: THREE.Group;
  readonly torso: THREE.Group;
  readonly head: THREE.Group;
  readonly leftArm: THREE.Group;
  readonly rightArm: THREE.Group;
  readonly props: Array<{
    mesh: THREE.Mesh;
    pulse?: { speed: number; phase: number };
  }>;
}
